import * as React from "react";
import { ThemeProvider as SCTheme } from "styled-components";
import { ConnectedRouter } from "connected-react-router";
import { history } from "./store/store";
import theme from "./theme";
import ToastList from "./components/Toasts/ToastList";
import Routes from "./Routes";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { localLogin, setScreenSize } from "./store/app/actions";
import useCompanySetting from "./hooks/useCompanySetting";
import ScrollToTop from "./ScrollToTop";
import { currentEnv, ENVIRONMENTS, SOCKET_URL } from ".";
import { getIntercomData, getToken } from "./store/localStorage";
import { handleSocketUpdates } from "./store/utils";
import { initIntercom } from "./integrations/Intercom";
let socket;

function App() {
  const dispatch = useDispatch();
  const [setting, settingLoading] = useCompanySetting();
  const isAuthenticated = useSelector((state) => !!state?.app?.user?.id);
  const user = useSelector((state) => state.app.user);
  const apartments = useSelector((state) => state.apartment.all);
  const indp = useSelector((state) => state.industrialPremises.all);
  const tenants = useSelector((state) => state.tenants.all);

  const [hasInitializedFreshchat, setHasInitializedFreshchat] =
    React.useState(false);

  const companyData = useSelector(
    (state) => state.tenantPortalSetting.companyData
  );

  const concatString = (listOne, listTwo) => {
    let mergedList = [...listOne, ...listTwo];
    return mergedList.join(", ");
  };
  const getStrRep = (apartments, indp) => {
    let apartmentList = [];
    let indpList = [];
    if (apartments) {
      apartmentList = Object.values(apartments)?.map((value) => {
        return value.str_representation;
      });
    }
    if (indp) {
      indpList = Object.values(indp)?.map((value) => {
        return value.str_representation;
      });
    }
    return concatString(apartmentList, indpList);
  };
  const getTenantCustomerId = (tenants) => {
    const match = Object.values(tenants)?.find(
      (t) => t.id === user?.tenant?.id
    );
    return match?.customer_id;
  };

  const handleSetScreenSize = () => {
    dispatch(setScreenSize());
  };

  React.useEffect(() => {
    dispatch(localLogin());
  }, []);

  React.useEffect(() => {
    handleSetScreenSize();
    window.addEventListener("resize", debounce(handleSetScreenSize, 100));

    return () => {
      window.removeEventListener("resize", debounce(handleSetScreenSize));
    };
  }, []);

  function connectSocket() {
    const token =
      currentEnv === ENVIRONMENTS.DEV
        ? "" // needs token from alpha for now
        : getToken().token;

    if (token) {
      socket = new WebSocket(`${SOCKET_URL}?Token=${token}`);

      socket.onmessage = function (e) {
        handleSocketUpdates(e, dispatch);
      };

      socket.onclose = function (e) {
        setTimeout(function () {
          connectSocket();
        }, 1000);
      };

      socket.onerror = function (err) {};
    }
  }

  React.useEffect(() => {
    if (isAuthenticated && !socket) {
      connectSocket();
    }

    if (!isAuthenticated && socket) {
      try {
        socket.close();
      } catch (e) {
        console.log("could not close socket");
      }
    }

    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (hasInitializedFreshchat) return;

    if (
      companyData &&
      companyData?.freshchat_token &&
      window?.fcWidget &&
      user
    ) {
      window.fcWidget.init({
        token: companyData?.freshchat_token,
        host: "https://wchat.eu.freshchat.com",
      });
      if (companyData?.freschat_attributes?.includes("email")) {
        window.fcWidget.user.setEmail(user.email);
      }
      if (companyData?.freschat_attributes?.includes("phone")) {
        window.fcWidget.user.setPhone(user.phone);
      }
      if (companyData?.freschat_attributes?.includes("name")) {
        window.fcWidget.user.setFirstName(user.str_representation);
      }

      setHasInitializedFreshchat(true);
    }
  }, [companyData, user]);

  React.useEffect(() => {
    const intercomData = getIntercomData();
    console.log("[INTERCOM]: a", { intercomData, user });

    if (!intercomData) return;

    if (user && Object.keys(user)?.length) {
      console.log("[INTERCOM]: b");

      const intercomData = getIntercomData();
      initIntercom({
        APP_ID: intercomData.token,
        user,
        intercomData,
      });
    }
  }, [companyData, user]);

  React.useEffect(() => {
    try {
      if (hasInitializedFreshchat) {
        if (companyData?.freschat_attributes?.includes("premises")) {
          window.fcWidget.user.setProperties({
            premises: getStrRep(apartments, indp),
          });
        }
      }
    } catch (error) {
      console.log(error);
    }
  }, [apartments, indp]);

  React.useEffect(() => {
    try {
      if (hasInitializedFreshchat) {
        if (companyData?.freschat_attributes?.includes("customer_id")) {
          window.fcWidget.user.setProperties({
            customer_id: getTenantCustomerId(tenants),
          });
        }
      }
    } catch (error) {
      console.log(error);
    }
  }, [tenants]);

  return (
    <SCTheme theme={theme(setting?.color_code)}>
      <ConnectedRouter history={history}>
        <ScrollToTop />
        <ToastList />
        {!settingLoading && <Routes />}
      </ConnectedRouter>
    </SCTheme>
  );
}

export default App;
